/* eslint-disable no-restricted-syntax */
/**
 * Settings type definitions for the Back Office Settings page
 * Aligned with backend API at /api/admin/settings/
 */

import { BaseResponse } from '@utils/types/shared/base';

// ============================================================================
// Settings Overview
// GET /api/admin/settings/overview
// ============================================================================

export type SettingsOverview = {
  total_languages: number;
  total_stt_configs: number;
  total_feature_flags: number;
  total_system_defaults: number;
};

// ============================================================================
// STT Configurations (Read-only views of LanguageConfig data)
// GET /api/admin/settings/stt-configs
// GET /api/admin/settings/stt-configs/{language_config_id}
// ============================================================================

export type SttConfig = {
  id: number;
  key: string;
  bcp47: string;
  name: string;
  is_enabled: boolean;
  asr_url: string | null;
  asr_alive: boolean;
  asr_model_location: string | null;
  asr_audio_rate: number;
  asr_version: string;
  asr_common_version: string;
  punctuation_url: string | null;
  punctuation_alive: boolean;
  punctuation_model_location: string | null;
  alignment_language: string | null;
  alignment_version: string;
};

export type SttConfigsResponse = BaseResponse<SttConfig[]> & {
  count: number;
  total_results: number;
};

export type SttConfigDetailResponse = BaseResponse<SttConfig> & {
  count: number;
  total_results: number;
};

// ============================================================================
// System Settings (Full CRUD)
// GET /api/admin/settings/system-settings
// POST /api/admin/settings/system-settings
// GET /api/admin/settings/system-settings/{setting_id}
// PATCH /api/admin/settings/system-settings/{setting_id}
// DELETE /api/admin/settings/system-settings/{setting_id}
// ============================================================================

export type SystemSettingValueType = 'string' | 'integer' | 'boolean' | 'json';
export type SystemSettingCategory = 'feature_flag' | 'default' | 'system';

export type SystemSetting = {
  id: number;
  key: string;
  value: string; // Stored as text, parse based on value_type
  value_type: SystemSettingValueType;
  category: SystemSettingCategory;
  description: string | null;
  is_active: boolean;
  created_at: string;
  last_modified: string;
};

export type SystemSettingsResponse = BaseResponse<SystemSetting[]> & {
  count: number;
  total_results: number;
};

export type SystemSettingDetailResponse = BaseResponse<SystemSetting> & {
  count: number;
  total_results: number;
};

// Create payload - POST /api/admin/settings/system-settings
export type CreateSystemSettingPayload = {
  key: string;
  value: string;
  value_type?: SystemSettingValueType; // defaults to 'string'
  category: SystemSettingCategory;
  description?: string;
  is_active?: boolean; // defaults to true
};

// Update payload - PATCH /api/admin/settings/system-settings/{id}
export type UpdateSystemSettingPayload = {
  value?: string;
  value_type?: SystemSettingValueType;
  description?: string;
  is_active?: boolean;
};

// ============================================================================
// Helper types for UI
// ============================================================================

// Settings tab types
export type SettingsTab = 'stt-configs' | 'flags' | 'defaults';

export const SETTINGS_TABS: SettingsTab[] = [
  'stt-configs',
  'flags',
  'defaults'
];

// Category display config
export type CategoryConfig = {
  label: string;
  color: string;
  bgColor: string;
};

export const CATEGORY_CONFIGS: Record<SystemSettingCategory, CategoryConfig> = {
  feature_flag: {
    label: 'Feature Flags',
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.1)'
  },
  default: {
    label: 'Defaults',
    color: '#3B82F6',
    bgColor: 'rgba(59, 130, 246, 0.1)'
  },
  system: {
    label: 'System',
    color: '#F59E0B',
    bgColor: 'rgba(245, 158, 11, 0.1)'
  }
};

// Helper to parse SystemSetting value based on value_type
export function parseSettingValue(
  setting: SystemSetting
): string | number | boolean | object {
  switch (setting.value_type) {
    case 'boolean':
      return setting.value.toLowerCase() === 'true';
    case 'integer':
      return parseInt(setting.value, 10);
    case 'json':
      try {
        return JSON.parse(setting.value);
      } catch {
        return setting.value;
      }
    case 'string':
    default:
      return setting.value;
  }
}

// Helper to stringify value for API
export function stringifySettingValue(
  value: string | number | boolean | object
): string {
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}
